import { useState } from 'react';
import { Canvas } from './components/Canvas.tsx';
import { ColorPreviewBoundary } from './components/ColorPreview.tsx';
import { ColorPaletteMosaic } from './components/ColorPalette.tsx';
import { Codi } from './components/Codi.tsx';
import {
  AuthenticationBoundary,
  WhenAuthenticated,
  WhenUnauthenticated,
} from './services/AuthService.tsx';
import { EnableDevJSONImport } from './services/ServiceConstants.tsx';
import {
  AuthForm,
  type AuthFormMode,
  LogoutButton,
} from './components/AuthForm.tsx';
import { WindowBoundary } from './components/Windows.tsx';
import { ConversationBoundary } from './services/ConversationService.tsx';
import { TypographyPreviewBoundary } from './components/TypographyPreview.tsx';
import { TypographyPalette } from './components/TypographyPalette.tsx';
import { InviteForm, RequireInviteCode } from './components/InviteForm.tsx';
import { ScreenPreviewBoundary } from './components/ScreenPreview.tsx';
import { ScreenPalette } from './components/ScreenPalette.tsx';
import { OOBEQuest, Quest } from './components/Quest.tsx';
import { VersionsBoundary } from './components/VersionsPreview.tsx';
import { VersionsPalette } from './components/VersionsPalette.tsx';
import { CodeBoundary } from './services/CodeService.tsx';
import { CodePalette } from './components/CodePalette.tsx';
import { NavigationPanel } from './components/NavigationPanel.tsx';
import { CleanSlate } from './components/CleanSlate.tsx';
import { ComponentsPreviewBoundary } from './components/ComponentsPreview.tsx';
import { ComponentsPalette } from './components/ComponentsPalette.tsx';
import { LivePreviewBoundary } from './components/LivePreview.tsx';
import { DeploymentPalette } from './components/DeploymentPalette.tsx';
import { BackendPalette } from './components/BackendPalette.tsx';
import { JSONImportPalette } from './components/JSONImportPalette.tsx';
import { ComponentPreviewDebugPalette } from './components/ComponentPreviewDebugPalette.tsx';
import { SubscriptionsPalette } from './components/SubscriptionsPalette.tsx';
import { PublicPreviewPage } from './components/PublicPreviewPage.tsx';
import { isPublicPreviewPath } from './services/PreviewLinks.ts';
import { LandingPage } from './landing/LandingPage.tsx';

export function App() {
  const [authFormMode, setAuthFormMode] = useState<AuthFormMode>('register');

  if (
    window.location.pathname === '/' ||
    window.location.pathname === '/privacy' ||
    window.location.pathname === '/privacy.html'
  ) {
    return <LandingPage />;
  }

  if (isPublicPreviewPath(window.location.pathname)) {
    return <PublicPreviewPage />;
  }

  const quest = OOBEQuest();

  function CanvasContent() {
    if (RequireInviteCode) {
      return (
        <>
          <WhenUnauthenticated>
            <AuthForm mode={authFormMode} onChangeMode={setAuthFormMode} />

            <InviteForm
              onGoToWaitlist={() => setAuthFormMode('waitlist')}
              onGoToLogin={() => setAuthFormMode('login')}
            />
          </WhenUnauthenticated>

          <WhenAuthenticated>
            <CodeBoundary>
              <NavigationPanel />

              <ColorPreviewBoundary>
                <TypographyPreviewBoundary>
                  <ComponentsPreviewBoundary>
                    <ColorPaletteMosaic />
                    <CodePalette />

                    <ScreenPreviewBoundary>
                      <LivePreviewBoundary>
                        {EnableDevJSONImport && <JSONImportPalette />}
                        {EnableDevJSONImport && (
                          <ComponentPreviewDebugPalette />
                        )}
                        <SubscriptionsPalette />
                        <CleanSlate />
                        <BackendPalette />
                        <DeploymentPalette />
                      </LivePreviewBoundary>
                      <Codi />
                      <ScreenPalette />
                      <ComponentsPalette />
                    </ScreenPreviewBoundary>

                    <TypographyPalette />
                  </ComponentsPreviewBoundary>
                </TypographyPreviewBoundary>
              </ColorPreviewBoundary>
            </CodeBoundary>

            <Quest name={quest.name} steps={quest.steps} />

            <div style={{ position: 'fixed', right: 16, bottom: 16 }}>
              <LogoutButton />
            </div>
            <VersionsPalette />
          </WhenAuthenticated>
        </>
      );
    }

    return (
      <>
        <WhenAuthenticated>
          <CodeBoundary>
            <NavigationPanel />
            <ColorPreviewBoundary>
              <TypographyPreviewBoundary>
                <ComponentsPreviewBoundary>
                  <ColorPaletteMosaic />

                  <ScreenPreviewBoundary>
                    <LivePreviewBoundary>
                      {EnableDevJSONImport && <JSONImportPalette />}
                      {EnableDevJSONImport && <ComponentPreviewDebugPalette />}
                      <SubscriptionsPalette />
                      <CleanSlate />
                      <BackendPalette />
                      <DeploymentPalette />
                    </LivePreviewBoundary>
                    <Codi />
                    <ScreenPalette />
                    <ComponentsPalette />
                  </ScreenPreviewBoundary>

                  <TypographyPalette />
                </ComponentsPreviewBoundary>
              </TypographyPreviewBoundary>
            </ColorPreviewBoundary>

            <Quest name={quest.name} steps={quest.steps} />

            <div style={{ position: 'absolute', right: 16, bottom: 16 }}>
              <LogoutButton />
            </div>
          </CodeBoundary>
        </WhenAuthenticated>

        <AuthForm mode={authFormMode} onChangeMode={setAuthFormMode} />
        <VersionsPalette />
      </>
    );
  }

  return (
    <WindowBoundary>
      <AuthenticationBoundary>
        <ConversationBoundary>
          <VersionsBoundary>
            <div
              style={{ width: '100%', height: '100vh', position: 'relative' }}
            >
              <Canvas />
              <CanvasContent />
            </div>
          </VersionsBoundary>
        </ConversationBoundary>
      </AuthenticationBoundary>
    </WindowBoundary>
  );
}

export default App;
